import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Film, Link2, Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 4 * 1024 * 1024;
const ACCEPT = ["video/mp4", "video/webm", "video/quicktime", "image/gif"];

const resolve = (url) => (url && url.startsWith("/api/") ? `${BACKEND}${url}` : url);
const isVideo = (ex) => ex.demo_type === "video" || (!ex.demo_type && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(ex.demo_url || ""));

export const ExerciseDemo = ({ workoutId, index, exercise, isAdmin, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(exercise.demo_url || "");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    const { data } = await api.patch(`/workouts/${workoutId}/exercises/${index}/demo`, { demo_url: url });
    onUpdated(data);
    setSaving(false);
    setEditing(false);
  };

  const upload = async (file) => {
    setError("");
    if (!ACCEPT.includes(file.type)) return setError("Use MP4, WebM, MOV or GIF.");
    if (file.size > 250 * 1024 * 1024) return setError("Max 250MB.");
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post(`/admin/uploads/${uploadId}/chunk?index=${i}`, fd);
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const { data } = await api.post(`/admin/uploads/${uploadId}/complete`, {
        filename: file.name, content_type: file.type, total_chunks: total, workout_id: workoutId, exercise_index: index,
      });
      setProgress(100);
      onUpdated(data);
      setTimeout(() => setProgress(null), 800);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed.");
      setProgress(null);
    }
  };

  const src = resolve(exercise.demo_url) || exercise.illustration;

  return (
    <div className="w-full" data-testid={`ex-demo-${index}`}>
      <div className="relative overflow-hidden aspect-square" style={{ background: "#0A0A0A", border: "1px solid #27272A" }}>
        {exercise.demo_url && isVideo(exercise) ? (
          <video src={resolve(exercise.demo_url)} className="w-full h-full object-cover" autoPlay loop muted playsInline data-testid={`ex-demo-video-${index}`} />
        ) : src ? (
          <img src={src} alt={`${exercise.name} demo`} className={`w-full h-full object-cover ${exercise.demo_url ? "" : "demo-loop"}`} data-testid={`ex-demo-img-${index}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600"><Film size={28} /></div>
        )}
        <span className="absolute bottom-2 left-2 eyebrow text-[9px] px-2 py-1" style={{ background: "rgba(0,0,0,0.7)", color: "#FF0055" }}>
          {exercise.demo_url ? "Demo" : "Form guide"}
        </span>
        {progress !== null && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ background: "rgba(10,10,10,0.85)" }} data-testid={`ex-demo-progress-${index}`}>
            <p className="font-display font-black text-2xl" style={{ color: "#FF0055" }}>{progress}%</p>
            <div className="w-2/3 h-1" style={{ background: "#27272A" }}><div className="h-1 transition-all" style={{ width: `${progress}%`, background: "#FF0055" }} /></div>
            <p className="eyebrow text-[9px]">{progress < 90 ? "Uploading" : progress < 100 ? "Saving" : "Done"}</p>
          </div>
        )}
      </div>
      {isAdmin && (
        <div className="mt-2">
          <input ref={fileRef} type="file" accept={ACCEPT.join(",")} className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`ex-demo-file-${index}`} />
          {editing ? (
            <div className="flex gap-2">
              <input className="input text-xs py-2" placeholder="https://… .mp4 / .gif" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`ex-demo-url-${index}`} />
              <button className="btn-primary text-[10px] px-3 py-2" onClick={save} disabled={saving} data-testid={`ex-demo-save-${index}`}>{saving ? "…" : "Save"}</button>
            </div>
          ) : (
            <div className="flex gap-3 flex-wrap">
              <button className="btn-ghost pl-0 text-[10px] inline-flex items-center gap-1" onClick={() => fileRef.current?.click()} disabled={progress !== null} data-testid={`ex-demo-upload-${index}`}>
                <Upload size={11} /> {exercise.demo_url ? "Replace clip" : "Upload clip"}
              </button>
              <button className="btn-ghost pl-0 text-[10px] inline-flex items-center gap-1" onClick={() => setEditing(true)} data-testid={`ex-demo-edit-${index}`}>
                <Link2 size={11} /> URL
              </button>
            </div>
          )}
          {error && <p className="text-[10px] mt-1" style={{ color: "#FF0055" }} data-testid={`ex-demo-error-${index}`}>{error}</p>}
        </div>
      )}
    </div>
  );
};
